import React, { useEffect, useState } from 'react'
import { ReactFitty } from "react-fitty";
import {
  Box,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tab,
  Tabs,
  Tooltip,
  Typography,
  CircularProgress,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import HomeIcon from '@mui/icons-material/HomeOutlined'
import PhoneIcon from '@mui/icons-material/PhoneOutlined'
import FavoriteIcon from '@mui/icons-material/FavoriteBorderOutlined'
import FavoriteFilledIcon from '@mui/icons-material/Favorite'
import LocationOnIcon from '@mui/icons-material/LocationOnOutlined'
import useSWR, { useSWRConfig } from 'swr'
import api from 'utils/api'
import { useIsMobile } from 'utils/UI'
import OfferMeta from '../OfferCard/components/OfferMeta'
import Barcode from './components/Barcode'
import Qrcode from './components/Qrcode'
import useFavoriteOffer from '../useFavoriteOffer'
import { revalidateClaimHistory, updateCachedClaimsRemaining } from '../cache'

const formatPhone = (input = '') => {
  const digits = input.replace(/\D/g, '').slice(0, 10)
  if (!digits) return ''
  if (digits.length < 4) return `(${digits}`
  if (digits.length < 7) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
}
const isPhoneFormatted = (phone) => /^\(\d{3}\) \d{3}-\d{4}$/.test(phone)
const formatPhoneIfNeeded = (phone) => {
  if (!phone) return phone
  return isPhoneFormatted(phone) ? phone : formatPhone(phone)
}

const codeType = {
  promoCode: 0,
  barcode: 1,
  qrCode: 2,
}

const firstAvailableCodeType = (claim) => {
  if (claim?.promoCode) return codeType.promoCode
  if (claim?.barcode) return codeType.barcode
  if (claim?.qrCode) return codeType.qrCode
  return undefined
}

const ClaimPopup = ({
  closePopup,
  description,
  distance,
  id,
  headline1,
  headline2,
  claims: claimsRemaining,
  endDate,
  active = true,
  address,
  showAddress,
  phone,
  showPhone,
  offerLocation,
  revalidateList,
  businessName,
  businessImageUrl,
}) => {
  const { isFavorite, isUpdating, toggleFavorite } = useFavoriteOffer(id, { revalidateList })
  const [showPhonePopup, setShowPhonePopup] = useState(false)
  const [showLocationPopup, setShowLocationPopup] = useState(false)
  const [showMapsChoice, setShowMapsChoice] = useState(false)

  const getResolvedAddress = () => {
    if (address) return address
    if (offerLocation?.local && offerLocation.local.length > 0) {
      const addr = offerLocation.local.find((a) => a.showAddress === true)
      if (addr) return addr.address
      return offerLocation.local[0].address
    }
    return null
  }
  const resolvedAddress = getResolvedAddress()
  const resolvedShowAddress = showAddress === true

  const handleOpenGoogleMaps = () => {
    if (!resolvedAddress) return
    window.open(`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(resolvedAddress)}`, '_blank')
    setShowMapsChoice(false)
    setShowLocationPopup(false)
  }
  const handleOpenAppleMaps = () => {
    if (!resolvedAddress) return
    window.open(`https://maps.apple.com/?q=${encodeURIComponent(resolvedAddress)}`, '_blank')
    setShowMapsChoice(false)
    setShowLocationPopup(false)
  }
  const handleDirectionsClick = () => {
    if (!resolvedAddress) return
    if (/iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream) {
      setShowMapsChoice(true)
    } else {
      handleOpenGoogleMaps()
    }
  }

  const { mutate } = useSWRConfig()
  const url = `/offer/${id}/claim`
  const { data, error } = useSWR(url, () => api.put(url), {
    revalidateOnFocus: false,
    shouldRetryOnError: false,
  })
  const [currentType, setCurrentType] = useState(() => firstAvailableCodeType(data))
  const isMobile = useIsMobile()
  const displayedClaimsRemaining = data?.claimsRemaining ?? claimsRemaining

  useEffect(() => {
    if (!data) {
      return
    }
    Promise.allSettled([
      Number.isFinite(data.claimsRemaining)
        ? updateCachedClaimsRemaining(id, data.claimsRemaining, mutate)
        : undefined,
      revalidateClaimHistory(mutate),
    ])
    const initialCodeType = firstAvailableCodeType(data)
    if (initialCodeType !== undefined) {
      setCurrentType(initialCodeType)
    }
  }, [data, id, mutate])

  return (
    <>
    <Dialog
      onClose={closePopup}
      fullScreen={isMobile}
      open
      fullWidth
      maxWidth="sm"
    >
      <Box sx={{ position: 'relative', px: 1, pt: 6, pb: 0 }}>
        <IconButton
          aria-label="close"
          onClick={closePopup}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: 'black',
          }}
        >
          <CloseIcon />
        </IconButton>
        <OfferMeta quantity={displayedClaimsRemaining} endDate={endDate} />
      </Box>
      <DialogTitle component="div">
        {businessImageUrl && (
          <Box height={30} sx={{ mb: 0.5 }}>
            <img src={businessImageUrl} height={30} alt="" />
          </Box>
        )}
        <Typography variant="h5">{headline1} | {headline2}</Typography>
        <Typography variant="body2" color="textSecondary">{distance}</Typography>
      </DialogTitle>
      <DialogContent>
        <Typography paragraph variant="body2">
          {description || 'No description for this offer'}
        </Typography>
        {!data && !error && (
          <Box p={4} display="flex" justifyContent="center">
            <CircularProgress />
          </Box>
        )}
        {error && (
          <Alert severity="error">
            {error.message || (typeof error.error === 'string' && error.error)
              || 'This offer could not be claimed. Try again later.'}
          </Alert>
        )}
        {data && (
          <Tabs centered onChange={(e, val) => setCurrentType(val)} value={currentType ?? false}>
            {data.promoCode && <Tab label="Promo code" value={codeType.promoCode} />}
            {data.barcode && <Tab label="Barcode" value={codeType.barcode} />}
            {data.qrCode && <Tab label="QR code" value={codeType.qrCode} />}
          </Tabs>
        )}
        {currentType === codeType.qrCode && data?.qrCode && <Qrcode code={data.qrCode} />}
        {currentType === codeType.barcode && data?.barcode && <Barcode code={data.barcode} />}
        {currentType === codeType.promoCode && data?.promoCode && (
          <Box
            borderRadius="4px"
            p={4}
            mb={1}
            style={{ border: '1px solid #ddd' }}
            display="flex"
            justifyContent="center"
          >
            <Typography variant="h3" color="primary" component={(props) => <ReactFitty {...props} maxSize="50" style={{ textAlign: 'center', width: '100%' }}/>}>
              {data.promoCode}
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ flexWrap: 'wrap', gap: 0, '& > *': { margin: 0 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', width: '100%', gap: 0 }}>
          {showPhone === true && (
            <Tooltip title="Call business" enterDelay={500} leaveDelay={50}>
              <span style={{ margin: 0, display: 'inline-flex' }}>
                <IconButton
                  aria-label="call business"
                  disabled={!active}
                  onClick={() => setShowPhonePopup(true)}
                  size="large"
                  sx={{ padding: '6px' }}
                >
                  <PhoneIcon />
                </IconButton>
              </span>
            </Tooltip>
          )}
          {resolvedShowAddress === true && (
            <Tooltip title="Directions" enterDelay={500} leaveDelay={50}>
              <span style={{ margin: 0, display: 'inline-flex' }}>
                <IconButton
                  aria-label="Directions"
                  disabled={!active}
                  onClick={() => setShowLocationPopup(true)}
                  size="large"
                  sx={{ padding: '6px' }}
                >
                  <LocationOnIcon />
                </IconButton>
              </span>
            </Tooltip>
          )}
          <Tooltip title="Visit website" enterDelay={500} leaveDelay={50}>
            <span style={{ margin: 0, display: 'inline-flex' }}>
              <IconButton
                aria-label="go to website"
                disabled={!active}
                href={`/api/common/offer_homepage/${id}`}
                target="_blank"
                rel="nofollow"
                size="large"
                sx={{ padding: '6px' }}
              >
                <HomeIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip
            title={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
            enterDelay={500}
            leaveDelay={50}
          >
            <span style={{ margin: 0, display: 'inline-flex' }}>
              <IconButton
                aria-label={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
                onClick={toggleFavorite}
                disabled={isUpdating}
                size="large"
                sx={{ padding: '6px' }}
              >
                {isFavorite ? <FavoriteFilledIcon color="error" /> : <FavoriteIcon />}
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </DialogActions>
    </Dialog>

    <Dialog open={showPhonePopup} onClose={() => setShowPhonePopup(false)}>
      <DialogTitle>{businessName ? `${businessName} ` : 'Business phone'}</DialogTitle>
      <DialogContent>
        {phone ? (
          <>
            <Typography variant="h6" component="div" sx={{ textAlign: 'center', py: 2 }}>
              {formatPhoneIfNeeded(phone)}
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<PhoneIcon />}
                href={`tel:${phone.replace(/\D/g, '')}`}
              >
                Call
              </Button>
            </Box>
          </>
        ) : (
          <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
            Phone number not available
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setShowPhonePopup(false)} color="primary">Close</Button>
      </DialogActions>
    </Dialog>

    <Dialog open={showLocationPopup} onClose={() => setShowLocationPopup(false)}>
      <DialogTitle>{businessName ? `${businessName} ` : 'Business address'}</DialogTitle>
      <DialogContent>
        {resolvedAddress ? (
          <>
            <Typography variant="h6" component="div" sx={{ textAlign: 'center', py: 2 }}>
              {resolvedAddress}
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<LocationOnIcon />}
                onClick={handleDirectionsClick}
              >
                Directions
              </Button>
            </Box>
          </>
        ) : (
          <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
            Address not available
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setShowLocationPopup(false)} color="primary">Close</Button>
      </DialogActions>
    </Dialog>

    <Dialog open={showMapsChoice} onClose={() => setShowMapsChoice(false)}>
      <DialogTitle>Choose maps app</DialogTitle>
      <DialogContent>
        <Typography variant="body1" sx={{ textAlign: 'center', py: 2 }}>
          Select your preferred maps application
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2, flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<LocationOnIcon />}
            onClick={handleOpenGoogleMaps}
            sx={{ minWidth: 140 }}
          >
            Google Maps
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<LocationOnIcon />}
            onClick={handleOpenAppleMaps}
            sx={{ minWidth: 140 }}
          >
            Apple Maps
          </Button>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setShowMapsChoice(false)} color="primary">Cancel</Button>
      </DialogActions>
    </Dialog>
  </>
  );
}

export default ClaimPopup
